import React, { useState, useEffect, useRef } from "react";

// Icons for FAQ and CTA sections
const ShieldCheckIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const BanknotesIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UsersIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const CalendarDaysIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const HeartSolidIcon = (props) => (
  <svg {...props} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const ArrowRightIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

const ClockIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const HeartIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const sectionRef = useRef(null);
  const cardRef = useRef(null);

  // ✅ EXACT API URL
  const API_BASE_URL = "https://api.donatepe.org";

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/admin/testimonials`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          setTestimonials(data.data);
        } else {
          throw new Error("Failed to fetch testimonials");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-reveal");
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (cardRef.current) observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, []);

  const nextSlide = () => {
    if (testimonials.length > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrent((prev) => (prev + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const prevSlide = () => {
    if (testimonials.length > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrent((prev) =>
        prev === 0 ? testimonials.length - 1 : prev - 1
      );
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const goToSlide = (index) => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrent(index);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return `${API_BASE_URL}${imagePath}`;
  };

  // Format amount in Indian currency format
  const formatIndianCurrency = (amount) => {
    if (!amount) return null;
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  if (loading) {
    return (
      <section className="relative bg-white py-12 md:py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-64 bg-gradient-to-r from-gray-100 to-gray-200 rounded-3xl"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative bg-white py-12 md:py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 text-center border border-rose-100 shadow-lg">
            <div className="w-20 h-20 mx-auto bg-rose-50 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600">Unable to load testimonials. Please try again later.</p>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return (
      <section className="relative bg-white py-12 md:py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 text-center border border-gray-100 shadow-lg">
            <div className="w-20 h-20 mx-auto bg-teal-50 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-gray-600">No testimonials available yet. Be the first to share your story!</p>
          </div>
        </div>
      </section>
    );
  }

  const testimonial = testimonials[current];
  const formattedAmount = formatIndianCurrency(testimonial.raised_amount);

  return (
    <>
      <section ref={sectionRef} className="relative bg-white pt-8 md:pt-12 lg:pt-16 pb-4 md:pb-6 lg:pb-8 overflow-hidden">
        
        {/* ===== LIGHT, AIRY BACKGROUND ===== */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-gradient-to-br from-teal-50/30 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 left-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-gradient-to-tr from-amber-50/30 via-transparent to-transparent rounded-full blur-3xl animate-float-delay"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[1000px] h-[600px] md:h-[1000px] bg-gradient-to-r from-emerald-50/20 via-teal-50/20 to-emerald-50/20 rounded-full blur-3xl animate-pulse-slow"></div>
          
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #14b8a6 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* ===== SECTION HEADER ===== */}
          <div className="text-center max-w-3xl mx-auto mb-6 md:mb-8 opacity-0 animate-header-reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/80 backdrop-blur-sm rounded-full border border-teal-100 shadow-sm mb-3 md:mb-4">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
              </span>
              <span className="text-[10px] md:text-xs font-medium text-teal-700 uppercase tracking-wider">
                Voices of trust
              </span>
            </div>
            
            <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 tracking-tight mb-2 md:mb-4">
              people like{' '}
              <span className="relative">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                  you
                </span>
                <span className="absolute -bottom-2 left-0 w-full h-2 md:h-3 bg-gradient-to-r from-teal-200/40 to-emerald-200/40 blur-sm"></span>
              </span>
            </h2>
            
            <p className="text-sm md:text-base lg:text-lg text-gray-500 max-w-2xl mx-auto px-4">
              who benefited from medical crowdfunding
            </p>
          </div>

          {/* ===== MAIN TESTIMONIAL CARD ===== */}
          {testimonials.length > 0 && (
            <div 
              ref={cardRef}
              className="relative max-w-3xl mx-auto opacity-0 translate-y-8 animate-card-reveal px-2 sm:px-0"
            >
              
              {/* Decorative elements - hidden on mobile */}
              <div className="hidden md:block absolute -top-6 -left-6 w-20 h-20 bg-teal-100/30 rounded-full blur-2xl animate-float-slow"></div>
              <div className="hidden md:block absolute -bottom-6 -right-6 w-20 h-20 bg-amber-100/30 rounded-full blur-2xl animate-float-delay"></div>

              {/* ===== TESTIMONIAL CARD ===== */}
              <div className={`
                relative bg-white/90 backdrop-blur-sm rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl border border-white/60 p-5 md:p-8 lg:p-10
                transition-all duration-600 ease-out
                ${isAnimating ? 'opacity-60 scale-95' : 'opacity-100 scale-100'}
              `}>
                
                {/* Gradient accent bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-emerald-500"></div>
                
                {/* Quote mark - adjusted for mobile */}
                <div className="absolute top-3 right-4 md:top-6 md:right-8 text-5xl md:text-8xl text-teal-100/50 font-serif">"</div>
                
                {/* Testimonial content */}
                <div className="relative mb-4 md:mb-6 lg:mb-8 pr-8">
                  <p className="text-gray-700 text-sm md:text-base lg:text-lg xl:text-xl leading-relaxed italic line-clamp-4 md:line-clamp-none">
                    "{testimonial.description}"
                  </p>
                </div>
                
                {/* Author info with profile image - Text always on left after image */}
                <div className="flex flex-col gap-4">
                  {/* Profile and name section - Always left-aligned */}
                  <div className="flex items-center gap-3 md:gap-4">
                    {/* Profile Image */}
                    <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full overflow-hidden border-2 border-teal-100 shadow-lg flex-shrink-0">
                      <img
                        src={getImageUrl(testimonial.image_url)}
                        alt={testimonial.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://images.unsplash.com/photo-1494790108755-2714420cc6d5?w=200&h=200&fit=crop";
                        }}
                      />
                    </div>
                    
                    {/* Name and relationship - Always on the left */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-sm md:text-base lg:text-lg">
                        {testimonial.title}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-500">
                        {testimonial.relationship || 'Beneficiary'}
                      </p>
                    </div>
                  </div>

                  {/* Amount raised - Below profile section on mobile, inline on desktop */}
                  {formattedAmount && (
                    <div className="flex justify-start md:justify-end md:absolute md:bottom-8 md:right-8 lg:bottom-10 lg:right-10">
                      <div className="text-left">
                        <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">Raised</p>
                        <p className="text-sm md:text-base lg:text-lg xl:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                          {formattedAmount}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ===== NAVIGATION ARROWS ON CARD - VISIBLE ON MOBILE ===== */}
                {testimonials.length > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 sm:hidden">
                    <button
                      onClick={prevSlide}
                      disabled={isAnimating}
                      className="group relative w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-md hover:shadow-lg transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-500"></div>
                      <svg 
                        className="w-4 h-4 mx-auto text-gray-600 group-hover:text-teal-600 transition-colors group-hover:-translate-x-0.5 transition-transform duration-300" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    {/* Dot indicators on mobile */}
                    <div className="flex items-center gap-2">
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToSlide(index)}
                          className={`transition-all duration-300 ${
                            index === current
                              ? 'w-6 h-2 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-full'
                              : 'w-2 h-2 bg-gray-300 rounded-full'
                          }`}
                        />
                      ))}
                    </div>

                    <button
                      onClick={nextSlide}
                      disabled={isAnimating}
                      className="group relative w-10 h-10 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md hover:shadow-lg transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-500"></div>
                      <svg 
                        className="w-4 h-4 mx-auto group-hover:translate-x-0.5 transition-transform duration-300" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== DESKTOP NAVIGATION (hidden on mobile) ===== */}
          {testimonials.length > 1 && (
            <>
              {/* Desktop Arrows - Hidden on mobile */}
              <div className="hidden sm:flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={prevSlide}
                  disabled={isAnimating}
                  className="group relative w-12 h-12 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500"></div>
                  <svg 
                    className="w-5 h-5 mx-auto text-gray-600 group-hover:text-teal-600 transition-colors group-hover:-translate-x-0.5 transition-transform duration-300" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={nextSlide}
                  disabled={isAnimating}
                  className="group relative w-12 h-12 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg hover:shadow-xl transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500"></div>
                  <svg 
                    className="w-5 h-5 mx-auto group-hover:translate-x-0.5 transition-transform duration-300" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Desktop Dot Navigation - Hidden on mobile */}
              <div className="hidden sm:flex items-center justify-center mt-4">
                <div className="flex items-center gap-3 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full border border-gray-100 shadow-sm">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className="group relative"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full blur-md transition-all duration-500 ${
                        index === current ? 'opacity-60 scale-150' : 'opacity-0 group-hover:opacity-40'
                      }`}></div>
                      <div className={`relative transition-all duration-500 ${
                        index === current
                          ? 'w-10 h-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-full'
                          : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400 rounded-full'
                      }`}></div>
                    </button>
                  ))}
                  
                  <span className="text-xs text-gray-400 ml-2">
                    {current + 1} / {testimonials.length}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ===== FAQ TEASER ===== */}
      <section className="bg-white pt-2 md:pt-4 pb-8 md:pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-8">
            <span className="text-sm font-medium text-teal-600 uppercase tracking-wider">Got questions?</span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mt-1 md:mt-2">Common questions about DonatePe</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
            {[
              { q: 'Is DonatePe really free?', a: 'Yes, 100% free. No platform fees, no hidden charges. You keep everything.', icon: ShieldCheckIcon },
              { q: 'How do I access my funds?', a: 'Funds are available in your account 24/7. Transfer whenever you need.', icon: BanknotesIcon },
              { q: 'Who can start a fundraiser?', a: 'Anyone with a cause — individuals, NGOs, communities.', icon: UsersIcon },
              { q: 'How long does a campaign run?', a: 'As long as you need. No deadlines, no pressure.', icon: CalendarDaysIcon }
            ].map((faq, i) => {
              const Icon = faq.icon;
              return (
                <div key={i} className="bg-gradient-to-br from-gray-50 to-white p-4 md:p-5 rounded-xl border border-gray-100 hover:shadow-md transition-shadow duration-300">
                  <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <Icon className="w-4 h-4 md:w-5 md:h-5 text-teal-500" />
                    <span className="text-sm md:text-base">{faq.q}</span>
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 ml-6 md:ml-7">{faq.a}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

   

      <style jsx>{`
        @keyframes headerReveal {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-header-reveal {
          animation: headerReveal 0.8s cubic-bezier(0.2, 0.9, 0.3, 1) forwards;
        }
        
        @keyframes cardReveal {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-card-reveal {
          animation: cardReveal 0.9s cubic-bezier(0.23, 1, 0.32, 1) 0.2s forwards;
        }
        
        @keyframes reveal {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        
        .animate-reveal {
          animation: reveal 0.6s ease forwards;
        }
        
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
            opacity: 0.3;
          }
          33% { 
            transform: translate(20px, -15px) scale(1.05); 
            opacity: 0.4;
          }
          66% { 
            transform: translate(-15px, 10px) scale(0.95); 
            opacity: 0.35;
          }
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        
        .animate-float-delay {
          animation: float 25s ease-in-out infinite reverse;
        }
        
        .animate-float-slow {
          animation: float 30s ease-in-out infinite;
        }
        
        @keyframes pulse-slow {
          0%, 100% { 
            opacity: 0.2; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.3; 
            transform: scale(1.05); 
          }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        
        .duration-600 {
          transition-duration: 600ms;
        }

        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @media (min-width: 768px) {
          .line-clamp-none {
            -webkit-line-clamp: unset;
            display: block;
          }
        }
      `}</style>
    </>
  );
};

export default Testimonials;